import { useInView } from '@cian/valuation-utils-component';
import { UIHeading5, UIText2 } from '@cian/ui-kit';
import * as React from 'react';

import { useImageLoadingHandlers } from './hooks';
import { RecommendationPriceDiscount as PriceDiscount } from '../RecommendationPriceDiscount';
import { TRecommendation } from '../../types/recommendation';

import * as styles from './styles.css';

interface IRecommendationProps extends TRecommendation {
  onClick(analyticsData?: object): void;
  onShowTrap(analyticsData?: object): void;
  favoriteButton: React.ReactNode;
  labels: React.ReactNode;
  onTrack(offerId: number | null, position: number, contextToken?: string): void;
  position: number;
}

const CALLBACK_DELAY = 1000;

export const Recommendation: React.FC<IRecommendationProps> = ({
  imageUrl,
  title: price,
  description,
  analyticsData,
  offerId,
  href,
  onClick,
  onShowTrap,
  favoriteButton,
  labels,
  onTrack,
  position,
  discount,
}) => {
  const [trapShown, setTrapShown] = React.useState(false);
  const timerRef = React.useRef<number>();

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  const { onLoad, onError } = useImageLoadingHandlers({
    domain: 'FavoriteRecommendations',
    src: imageUrl,
  });

  React.useEffect(() => {
    if (!offerId && inView && !trapShown) {
      onShowTrap(analyticsData);
      setTrapShown(true);
    }

    // копируется логика ActionAfterViewObserver, так как он не умеет не оборачивать компонент в DIV
    if (inView) {
      timerRef.current = window.setTimeout(() => {
        onTrack(offerId, position);

        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }, CALLBACK_DELAY);

      return () => {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      };
    } else {
      return undefined;
    }
  }, [inView, offerId, analyticsData, onShowTrap, trapShown, onTrack, position]);

  return (
    <a
      ref={ref}
      onClick={() => onClick(analyticsData)}
      href={href}
      target="_blank"
      className={styles['container']}
      rel="noreferrer"
    >
      {labels}
      <span className={styles['favorite']}>{favoriteButton}</span>
      <img src={imageUrl} className={styles['image']} onLoad={onLoad} onError={onError} />
      <div className={styles['price']}>
        <UIHeading5>{discount ? discount.newPrice : price}</UIHeading5>
        {discount && <PriceDiscount oldPrice={discount.oldPrice} percent={discount.percent} />}
      </div>
      {description && (
        <div className={styles['description']}>
          <UIText2>{description}</UIText2>
        </div>
      )}
    </a>
  );
};
